import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  GeoAltFill,
  CashStack,
  CheckCircleFill,
  ChevronDown,
  Dash,
  Plus,
} from "react-bootstrap-icons";
import { decryptData } from "../../utils/crypto";
import {
  createCustomerOrder,
  getAllCustomerConfigList,
} from "../../services/customerServices";
import Utils from "../../utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { notify } from "../../components/Common/Alerts";
import { formatCurrencyINR } from "../../utils/function";
import config from "../../config";

const OrderSummaryView = ({
  store,
  cartData,
  onBack,
  customerDetailsData,
  setCartData,
  setSelectedSProductStore,
  setShowCatalogue,
  setShowSummary,
  setUpdateModel,
  setPinModelOpen,
  setPinData,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [method, setMethod] = useState(1);
  const [configList, setConfigList] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [placeOrderModel, setPlaceOrderModel] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const [formData, setFormData] = useState({
    full_address: "",
  });
  const storedData = JSON.parse(localStorage.getItem(config.login_data));

  useEffect(() => {
    if (customerDetailsData?.full_address) {
      setFormData((prev) => ({
        ...prev,
        full_address: customerDetailsData.full_address,
      }));
    }
  }, [customerDetailsData?.full_address]);

  useEffect(() => {
    getAllCustomerConfigList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setConfigList(decrypted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  const updateQty = (product, qtyChange, unit, isDirect = false) => {
    setCartData((prevCart) => {
      // Find the product by ID only (ignore unit_id)
      const existingIndex = prevCart.findIndex(
        (item) => item.id === product.id,
      );

      let newCart = [...prevCart];

      if (existingIndex >= 0) {
        // Update existing qty
        const existingItem = newCart[existingIndex];
        const newQty = isDirect ? qtyChange : existingItem.qty + qtyChange;

        if (newQty <= 0) {
          // Remove from cart if qty <= 0
          newCart.splice(existingIndex, 1);
        } else {
          // Update the item with new qty and latest unit info
          newCart[existingIndex] = {
            ...existingItem,
            qty: newQty,
            unit_id: unit.unit_id,
            unit_name: unit.unit_name,
            unit_price: Number(unit.selling_price),
          };
        }
      } else {
        // Add new item to cart
        if (qtyChange > 0) {
          newCart.push({
            ...product,
            qty: qtyChange,
            unit_id: unit.unit_id,
            unit_name: unit.unit_name,
            unit_price: Number(unit.selling_price),
          });
        }
      }

      return newCart;
    });
  };

  // const handleQtyInputChange = (product, unit, value) => {
  //   let qty = Number(value);
  //   if (isNaN(qty) || qty < 0) qty = 0;
  //   updateQty(product, qty, unit, true);
  // };

  const handleQtyInputChange = (product, unit, value) => {
    // store temporary input
    setInputValues((prev) => ({
      ...prev,
      [product.id]: value,
    }));

    // if valid number → update cart
    if (value !== "" && !isNaN(value)) {
      updateQty(product, Number(value), unit, true);
    }
  };

  const handleBlur = (product, unit) => {
    const value = inputValues[product.id];

    if (value === "" || Number(value) === 0) {
      updateQty(product, 0, unit, true);

      // clear local state
      setInputValues((prev) => {
        const updated = { ...prev };
        delete updated[product.id];
        return updated;
      });
    }
  };

  const totalItems = new Set(cartData.map((item) => item.id)).size;

  const totalPrice = cartData.reduce((sum, item) => {
    const unitBase = Number(item.unit_qty || 1);
    return sum + (item.qty / unitBase) * item.unit_price;
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (method === 2 && !formData?.full_address) {
      notify.error("Please select an address");
      setPlaceOrderModel(false);
      return;
    }

    setBtnLoading(true);

    const filterProduct = cartData.map((item) => ({
      product_id: item.id,
      name: item.name,
      quantity: item.qty,
      unit_id: item.unit_id,
      unit: item.unit_short_name,
      total_price: item.calculated_price,
      price: item.unit_price,
      unit_qty: item.unit_qty,
    }));

    const payload = {
      store_id: store?.id,
      delivery_address: formData
        ? formData?.full_address
        : customerDetailsData?.full_address,
      latitude: "23.0225",
      longitude: "72.5714",
      payment_method: 1,
      order_type: method,
      items: filterProduct,
      total_amount: totalPrice,
    };

    try {
      const response = await createCustomerOrder(payload);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      setCartData([]);
      setSelectedSProductStore(null);
      setShowCatalogue(false);
      setShowSummary(false);
      setUpdateModel(false);
      setPinModelOpen(false);
      setPinData(null);
      setPlaceOrderModel(false);

      navigate("/success", { state: { item: decrypted.data } });
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#FDFCFB] dark:bg-slate-950 font-sans overflow-hidden transition-colors duration-300">
      {/* Desktop Header */}
      <header className="bg-[#E67E22] dark:bg-orange-600 p-5 flex items-center gap-4 shadow-lg hidden md:flex shrink-0">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center text-slate-800 dark:text-white"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-white font-black text-xl tracking-tight">
          {t("orderSummary")}
        </h1>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-32 md:p-10 lg:p-12">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 items-start">
          {/* LEFT COLUMN: Order Details & Products (Flex-1) */}
          <div className="flex-1 w-full space-y-8">
            {/* Fulfillment Toggle */}
            <div className="flex bg-slate-100 dark:bg-slate-900 p-1.5 rounded-2xl w-full lg:w-fit min-w-[320px]">
              {configList?.data?.order_type?.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setMethod(type.value)}
                  className={`flex-1 px-8 py-3 rounded-xl font-bold text-sm transition-all duration-200 ${
                    method === type.value
                      ? "bg-[#E67E22] text-white shadow-lg shadow-orange-500/20"
                      : "text-slate-500 hover:text-slate-700 dark:text-slate-400"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>

            {/* Address Card (If active) */}
            {method === 2 && (
              <div className="hidden md:block bg-white dark:bg-slate-900 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] block mb-4">
                  {t("deliveryAddress")}
                </label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-2xl text-[#E67E22] dark:text-orange-500">
                      <GeoAltFill size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-700 dark:text-slate-200 text-sm">
                        {customerDetailsData?.full_address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {method === 2 && (
              <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm md:hidden">
                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-3">
                  {t("deliveryAddress")}
                </label>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-[#E67E22] dark:text-orange-500">
                    <GeoAltFill />
                  </div>

                  <textarea
                    type="text"
                    placeholder="Enter delivery address"
                    value={formData.full_address || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        full_address: e.target.value,
                      }))
                    }
                    className="flex-1 bg-transparent outline-none font-bold text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400"
                    rows={3}
                  />
                </div>
              </div>
            )}

            {method === 1 && (
              <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm md:hidden">
                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-3">
                  {t("pickupAddress")}
                </label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-[#E67E22] dark:text-orange-500">
                      <GeoAltFill />
                    </div>
                    <p className="font-bold text-slate-700 dark:text-slate-200 text-sm">
                      {store?.store_address}
                    </p>
                  </div>
                  <ChevronLeft className="rotate-180 text-slate-300 dark:text-slate-600" />
                </div>
              </div>
            )}

            {method === 1 && (
              <div className="hidden md:block bg-white dark:bg-slate-900 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] block mb-4">
                  {t("deliveryAddress")}
                </label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-2xl text-[#E67E22] dark:text-orange-500">
                      <GeoAltFill size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-700 dark:text-slate-200 text-sm">
                        {store?.store_address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <div className="flex items-center justify-between px-2">
                <h3 className="text-sm font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">
                  {t("itemsInCart")}
                </h3>
                <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-4 py-1.5 rounded-full text-xs font-black">
                  {cartData.length} {t("items")}
                </span>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {cartData.length === 0 ? (
                  <div className="col-span-full py-20 text-center">
                    <button
                      onClick={() => setShowSummary(false)}
                      className="px-8 py-4 bg-orange-500 text-white font-bold rounded-2xl hover:bg-orange-600 transition"
                    >
                      {t("selectOtherItem")}
                    </button>
                  </div>
                ) : (
                  cartData?.map((item) => {
                    const qty =
                      inputValues[item.id] !== undefined
                        ? inputValues[item.id]
                        : item?.qty || 0;
                    const price = Number(
                      item.unit_price ?? item.selling_price ?? 0,
                    );

                    return (
                      <div
                        key={`${item.id}-${item.unit_id}`}
                        className="group bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-5 rounded-[2rem] shadow-sm hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-20 h-20 bg-orange-50/50 dark:bg-orange-900/10 rounded-bl-full -mr-6 -mt-6" />
                        <div className="flex items-start sm:items-center gap-4 sm:gap-6 relative z-10">
                          <div className="flex gap-4 relative z-10">
                            <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-3xl flex items-center justify-center shrink-0 border border-slate-50 dark:border-slate-700">
                              <span className="text-3xl sm:text-4xl filter drop-shadow-sm">
                                {item?.icon || "📦"}
                              </span>
                            </div>
                          </div>

                          {/* Content Area */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-1">
                              <h4 className="text-xl font-black text-slate-800 dark:text-white truncate">
                                {item.name}
                              </h4>
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                                {t("packing")}:{" "}
                                <span className="text-slate-500 dark:text-slate-400 font-medium">
                                  {item.name} ({item.qty}{" "}
                                  {item?.unit_short_name})
                                </span>
                              </p>

                              <div className="mt-4 pt-3 border-t border-dashed border-slate-100 dark:border-slate-800 flex justify-between items-end">
                                <div>
                                  <p className="text-[9px] font-black text-slate-400 uppercase">
                                    {t("unitPrice")}
                                  </p>
                                  <p className="font-bold text-slate-600 dark:text-slate-300">
                                    {formatCurrencyINR(
                                      Number(
                                        item.unit_price ??
                                          item.selling_price ??
                                          0,
                                      ),
                                    )}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-[9px] font-black text-orange-500 uppercase">
                                    {t("productTotal")}
                                  </p>
                                  <p className="text-xl font-black text-slate-900 dark:text-white">
                                    {formatCurrencyINR(item.calculated_price)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <aside className="w-full lg:w-[400px] lg:sticky lg:top-10 space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 shadow-2xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-800">
              <h3 className="text-sm font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-8">
                {t("orderCalculation")}
              </h3>

              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 mb-8 border border-slate-100 dark:border-slate-700">
                <label className="text-[10px] font-black text-slate-400 uppercase block mb-3">
                  {t("paymentMethod")}
                </label>
                <div className="flex items-center gap-4 text-slate-700 dark:text-slate-200 font-bold">
                  <CashStack className="text-[#E67E22]" size={22} />
                  <span>{t("cashOnStore")}</span>
                  <CheckCircleFill
                    className="ml-auto text-green-500"
                    size={18}
                  />
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex justify-between text-slate-500 font-medium">
                  <span>{t("subtotal")}</span>
                  <span>{formatCurrencyINR(totalPrice)}</span>
                </div>

                <div className="pt-6 border-t-2 border-dashed border-slate-100 dark:border-slate-800">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-slate-800 dark:text-slate-100">
                      {t("total")}:
                    </span>
                    <span className="text-3xl font-black text-[#E67E22] dark:text-orange-500">
                      {formatCurrencyINR(totalPrice)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setPlaceOrderModel(true)}
                disabled={btnLoading || cartData.length === 0}
                className={`hidden md:flex w-full py-5 rounded-2xl font-black uppercase tracking-widest shadow-xl transition-all flex items-center justify-center gap-3 ${
                  btnLoading
                    ? "bg-orange-400 cursor-not-allowed text-white"
                    : "bg-[#E67E22] dark:bg-orange-600 hover:scale-[1.02] active:scale-95 text-white shadow-orange-500/30"
                }`}
              >
                {btnLoading && (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                )}
                {t("placeOrder")}
              </button>
            </div>
          </aside>
        </div>
      </main>

      {cartData?.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-6 md:hidden z-40">
          <div className="absolute inset-0 bg-gradient-to-t from-[#FDFCFB] via-[#FDFCFB]/90 to-transparent dark:from-slate-950 dark:via-slate-950/90 -z-10" />

          <div className="max-w-md mx-auto">
            <button
              type="button"
              onClick={() => setPlaceOrderModel(true)}
              className={`w-full py-5 rounded-[2rem] font-black uppercase tracking-widest shadow-xl transition-all ${
                btnLoading
                  ? "bg-orange-400 cursor-not-allowed"
                  : "bg-[#E67E22] dark:bg-orange-600 hover:scale-[1.02] active:scale-95 text-white shadow-orange-100 dark:shadow-none"
              }`}
            >
              {t("placeOrder")}
            </button>
          </div>
        </div>
      )}

      {placeOrderModel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white dark:bg-[#161e2d] rounded-[2.5rem] p-8 w-full max-w-md shadow-2xl animate-fade-in border border-slate-100 dark:border-slate-800">
            <h2 className="text-2xl font-black text-slate-800 dark:text-white mb-3">
              {t("confirmPlaceOrder")}
            </h2>
            <p className="text-slate-500 dark:text-gray-400 mb-8 leading-relaxed font-medium">
              {t("placeOrderDescription")}
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setPlaceOrderModel(false)}
                className="flex-1 py-4 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold hover:bg-slate-200 transition-colors"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={btnLoading}
                className={`flex-1 py-4 rounded-2xl font-bold transition-all shadow-lg ${
                  btnLoading
                    ? "bg-red-400 cursor-not-allowed"
                    : "bg-red-500 hover:bg-red-600 text-white shadow-red-500/20"
                }`}
              >
                {btnLoading ? t("placeOrders") : t("yesPlaceOrder")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderSummaryView;
